import QueryString from 'query-string';
import { toastr } from 'react-redux-toastr';
import {
  CHART_OF_ACCOUNTS_ENDPOINT,
  EXPENSE_SAP_MIGRATION_ENDPOINT,
  MIGRATE_EXPENSE_SAP_MIGRATION_ENDPOINT,
} from '../../config/endpoints';
import {
  DEFAULT_GET_CONFIG,
  DEFAULT_POST_CONFIG,
  DEFAULT_PUT_CONFIG,
  EXCEL_GET_CONFIG,
} from '../../config/rest';
import { isErrorResponse } from '../../utils/error-handlers';
import {
  CLEAR_CHART_OF_ACCOUNTS,
  CLEAR_EXPENSE_SAP_MIGRATION,
  CLEAR_EXPENSE_SAP_MIGRATIONS,
  CLEAR_EXPENSE_SAP_MIGRATIONS_BY_EXPENSE,
  FLAG_EXPENSE_SAP_MIGRATION_ACTIVITY,
  FLAG_GETTING_CHART_OF_ACCOUNTS,
  FLAG_GETTING_EXPENSE_SAP_MIGRATIONS,
  GET_CHART_OF_ACCOUNTS,
  GET_EXPENSE_SAP_MIGRATION,
  GET_EXPENSE_SAP_MIGRATIONS,
  GET_EXPENSE_SAP_MIGRATIONS_BY_EXPENSE,
} from '../types';
import { DATE_FORMAT } from '../../config/locale';
import { tzNormalizeDate } from '../../utils/date';

const flagExpensesSapMigration = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_EXPENSE_SAP_MIGRATIONS,
    payload: flag,
  });

const getExpensesSapMigration =
  async (tableFilters) =>
  async (dispatch) => {
    try {
      dispatch(flagExpensesSapMigration(true));
      const query = tableFilters;
      const url = `${EXPENSE_SAP_MIGRATION_ENDPOINT}?${QueryString.stringify(
        query,
      )}`;
      const promise = await fetch(url, DEFAULT_GET_CONFIG);
      await isErrorResponse(promise);
      const response = await promise.json();
      dispatch({
        type: GET_EXPENSE_SAP_MIGRATIONS,
        payload: response,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagExpensesSapMigration(false));
    }
  };

const clearExpensesSapMigration = () => (dispatch) =>
  dispatch({
    type: CLEAR_EXPENSE_SAP_MIGRATIONS,
  });

const flagExpenseSapMigration = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_EXPENSE_SAP_MIGRATION_ACTIVITY,
    payload: flag,
  });

const clearExpenseSapMigration = () => (dispatch) =>
  dispatch({
    type: CLEAR_EXPENSE_SAP_MIGRATION,
  });

const putExpenseSapMigration =
  (id, expenseSapMigration) => async (dispatch) => {
    try {
      dispatch(flagExpenseSapMigration(true));

      const url = `${EXPENSE_SAP_MIGRATION_ENDPOINT}/${id}`;

      const promise = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        body: JSON.stringify(expenseSapMigration),
      });

      await isErrorResponse(promise);

      const response = await promise.json();

      if (response) {
        toastr.success('Éxito', 'El egreso a migrar ha sido actualizado.');
        dispatch(clearExpenseSapMigration());

        return response;
      }
      return null;
    } catch (error) {
      toastr.error('Error', error.message);
      throw error;
    } finally {
      dispatch(flagExpenseSapMigration(false));
    }
  };

const postMigrateExpenseSapMigration =
  ({ expenseSapMigrationIds }) =>
  async (dispatch) => {
    try {
      dispatch(flagExpensesSapMigration(true));

      const url = `${MIGRATE_EXPENSE_SAP_MIGRATION_ENDPOINT}`;

      const promise = await fetch(url, {
        ...DEFAULT_POST_CONFIG,
        body: JSON.stringify({ expenseSapMigrationIds }),
      });

      await isErrorResponse(promise);

      const response = await promise.json();
      if (response) {
        dispatch(clearExpensesSapMigration());
        return response;
      }
      return null;
    } catch (error) {
      toastr.error('Error', error.message);
      throw error;
    } finally {
      dispatch(flagExpensesSapMigration(false));
    }
  };

  const getExpenseSapMigration =
  async (id) =>
  async (dispatch) => {
    try {
      dispatch(flagExpenseSapMigration(true));
      const url = `${EXPENSE_SAP_MIGRATION_ENDPOINT}/${id}`;
      const promise = await fetch(url, DEFAULT_GET_CONFIG);
      await isErrorResponse(promise);
      const response = await promise.json();
      dispatch({
        type: GET_EXPENSE_SAP_MIGRATION,
        payload: response,
      });
      return response
    } catch ({ message }) {
      toastr.error('Error', message);
      return null
    } finally {
      dispatch(flagExpenseSapMigration(false));
    }
  };

const flagChartOfAccounts = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_CHART_OF_ACCOUNTS,
    payload: flag,
  });

const getChartOfAccounts = () => async (dispatch) => {
  try {
    dispatch(flagChartOfAccounts(true));
    
    const url = `${CHART_OF_ACCOUNTS_ENDPOINT}`;
    
    const promise = await fetch(url, DEFAULT_GET_CONFIG);
    await isErrorResponse(promise);
    const response = await promise.json();
    
    dispatch({
      type: GET_CHART_OF_ACCOUNTS,
      payload: response,
    });
  } catch ({ message }) {
    toastr.error('Error', message);
  } finally {
    dispatch(flagChartOfAccounts(false));
  }
};

const clearChartOfAccounts = () => (dispatch) =>
  dispatch({
    type: CLEAR_CHART_OF_ACCOUNTS,
  });

const getExpenseSapMigrationExcel =
  (tableFilters) =>
  async (dispatch) => {
    try {
      dispatch(flagExpensesSapMigration(true));

      const query = tableFilters;
      const url = `${EXPENSE_SAP_MIGRATION_ENDPOINT}/export?${QueryString.stringify(
        query,
      )}`;

      const response = await fetch(url, EXCEL_GET_CONFIG);

      await isErrorResponse(response, null, dispatch);

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;

      let fileName = `egresos-sap-${tzNormalizeDate({
        format: DATE_FORMAT,
      })}.xlsx`;

      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) {
          [, fileName] = match;
        }
      }

      link.setAttribute('download', fileName);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagExpensesSapMigration(false));
    }
  };

export const getExpenseSapMigrationsByExpense = (expenseId) => async (dispatch) => {
  try {
    const url = `${EXPENSE_SAP_MIGRATION_ENDPOINT}/expense/${expenseId}/edited`;
    const promise = await fetch(url, DEFAULT_GET_CONFIG);
    await isErrorResponse(promise);
    const response = await promise.json();
    dispatch({
      type: GET_EXPENSE_SAP_MIGRATIONS_BY_EXPENSE,
      payload: response,
    });
  } catch ({ message }) {
    toastr.error('Error', message);
  }
};

export const clearExpenseSapMigrationsByExpense = () => (dispatch) =>
  dispatch({ type: CLEAR_EXPENSE_SAP_MIGRATIONS_BY_EXPENSE });

export {
  getExpensesSapMigration,
  clearExpensesSapMigration,
  putExpenseSapMigration,
  postMigrateExpenseSapMigration,
  getChartOfAccounts,
  clearChartOfAccounts,
  getExpenseSapMigration,
  getExpenseSapMigrationExcel
};
